/**
 * @module projection-fold
 * @category Internal
 *
 * Fold engine behind `projection(name).of(state)` — maintains per-stream
 * folded states in a bounded in-memory cache and flushes one row per dirty
 * stream per round, so write amplification tracks the distinct-key count
 * instead of the event count.
 *
 * The flush payload deliberately has no type of its own: a state
 * projection flushes the cache layer outward — the rows ARE the
 * streams' {@link CacheEntry} values.
 *
 * Correctness discipline:
 * - The engine runs as the projection's batch handler, so the watermark
 *   acks only after a fully-flushed batch — fold work is never
 *   acknowledged before it is durable.
 * - On first sight of a stream the engine loads its head state through
 *   the same `load()` the command path uses (cache, snapshots and all).
 *   The loaded snapshot carries its own head position (`version` and the
 *   global event `id`), captured atomically with `state`, so the engine
 *   never pairs a stale state with a newer head id read separately from
 *   the cache (ACT-1204). Fetched events at or below the loaded id are
 *   skipped, later ones fold through the state's own patch reducers.
 * - Eviction under `maxCachedStates` pressure flushes the evictee first
 *   (flush-before-evict) — eviction never loses folded work.
 */
import type {
  BatchHandler,
  CacheEntry,
  Schema,
  Schemas,
  State,
} from "../types/index.js";
import type { FoldConfig } from "./config.js";
import { bare_patch, load, type PatchFn } from "./event-sourcing.js";
import { pii_strip } from "./sensitive.js";

// The fold config schema, defaults, and resolver live in `./config.js` (the
// single home for builder-facing config bags). This module keeps the fold
// engine that consumes the resolved config.

/**
 * A stream's in-flight fold: a mutable {@link CacheEntry} plus the
 * engine's `dirty` flag. Mutable because the hot loop updates one
 * object per stream rather than allocating per event; the required
 * frontier fields are what keep a warm-cache load from ever
 * re-applying history. Stripping `dirty` at the flush boundary yields
 * the entry — the flush payload IS the cache entry.
 */
type Fold<TState extends Schema> = {
  -readonly [K in keyof CacheEntry<TState>]: CacheEntry<TState>[K];
} & { dirty: boolean };

/**
 * Internal handle a fold handler exposes so the orchestrator can drop its
 * process-local state (#1466).
 *
 * A rebuild must not trust a per-Act cache: `reset` rewinds the watermark and
 * replays from the beginning, and every replayed event is at or below the
 * cached head, so the fold takes its already-folded branch and re-flushes
 * whatever it happens to hold. If that cache is stale, the rebuild writes the
 * staleness back out — the one outcome a rebuild exists to prevent. Dropping
 * the cache makes the next batch re-load head state from the store, which is
 * authoritative by definition.
 *
 * A symbol on the function keeps `BatchHandler` a plain function type; nothing
 * here is re-exported from `src/index.ts`.
 */
export const FOLD_RESET = Symbol("act.fold.reset");

/** A batch handler that owns a fold cache it can be told to drop. */
export type ResettableBatchHandler<TEvents extends Schemas> =
  BatchHandler<TEvents> & {
    readonly [FOLD_RESET]?: () => void;
  };

/**
 * Build the batch handler that folds a state's events into per-stream rows.
 * The returned closure is long-lived (one per built projection): its cache
 * survives across drain cycles, so warm streams fold without I/O. It carries
 * a {@link FOLD_RESET} handle so a rebuild can drop that cache.
 */
export function make_fold_handler<
  TState extends Schema,
  TEvents extends Schemas,
  TActions extends Schemas,
>(
  me: State<TState, TEvents, TActions>,
  flush: (rows: ReadonlyArray<CacheEntry<TState>>) => Promise<void>,
  config: FoldConfig,
  patch_fn: PatchFn = bare_patch,
  sensitive_fields: (event_name: string) => readonly string[]
): BatchHandler<TEvents> {
  // First-sight head loads must apply the SAME PII treatment as the warm
  // path (which folds `pii_strip`ped events handed in by the batch handler):
  // sensitive keys removed entirely, not gated to `[REDACTED]`. `load()`
  // folds over `me.view`, so compose the view with `pii_strip` for sensitive
  // events — otherwise an actorless head load would gate to `[REDACTED]` and
  // the projected row would differ from the warm path by cache warmth (#1320).
  // For a non-PII projection every lookup is empty, so `load_me === me`
  // behaviorally.
  const load_me: State<TState, TEvents, TActions> = {
    ...me,
    view: (raw, actor) => {
      const viewed = me.view(raw, actor);
      const fields = sensitive_fields(viewed.name as string);
      return fields.length
        ? (pii_strip(viewed as never, fields) as typeof viewed)
        : viewed;
    },
  };
  // Insertion-ordered Map as LRU: first key is the oldest. A promote is
  // delete + re-insert. Not the shared LruMap — eviction here must await
  // a flush, and a sync auto-evicting set() would drop folded work.
  const cache = new Map<string, Fold<TState>>();

  const row = ({ dirty: _, ...row }: Fold<TState>): CacheEntry<TState> => row;

  const flush_dirty = async () => {
    const rows: CacheEntry<TState>[] = [];
    const flushed: Fold<TState>[] = [];
    for (const f of cache.values())
      if (f.dirty) {
        rows.push(row(f));
        flushed.push(f);
      }
    if (rows.length === 0) return;
    await flush(rows);
    for (const f of flushed) f.dirty = false;
  };

  /**
   * Load a stream's head state and seat it in the cache.
   *
   * The loaded state and its head position (`version`, global `id`,
   * `patches`, `snaps`) are captured atomically inside `load()` — even on a
   * warm cache hit where `snapshot.event` is undefined. Reading the head id
   * back from the cache separately (ACT-1204) opened a TOCTOU window: a
   * concurrent `action()` committing between the two awaits pairs this
   * OLDER state with a NEWER event_id, and the frontier guard below then
   * permanently skips the intervening events. Dirty from the start: the row
   * must be written at least once.
   */
  const first_sight = async (stream: string): Promise<Fold<TState>> => {
    if (cache.size >= config.maxCachedStates) {
      // flush-before-evict: the oldest entry leaves only after its folded
      // work is durable.
      const oldest = cache.keys().next().value as string;
      const evictee = cache.get(oldest) as Fold<TState>;
      if (evictee.dirty) await flush([row(evictee)]);
      cache.delete(oldest);
    }
    const snapshot = await load(load_me, { stream }, undefined, patch_fn);
    const seated: Fold<TState> = {
      stream,
      state: snapshot.state,
      version: snapshot.version,
      event_id: snapshot.id,
      patches: snapshot.patches,
      snaps: snapshot.snaps,
      dirty: true,
    };
    cache.set(stream, seated);
    return seated;
  };

  const handler: ResettableBatchHandler<TEvents> = async (events) => {
    let folded_since_flush = 0;
    for (const event of events) {
      const stream = event.stream;
      let fold = cache.get(stream);
      if (fold) {
        // promote to most-recent
        cache.delete(stream);
        cache.set(stream, fold);
      } else {
        fold = await first_sight(stream);
      }
      // Fold forward only across a CONTIGUOUS version step (#1465). The
      // cache is per-Act but the subscription watermark is shared, so a
      // worker can be handed an event whose predecessors were drained by a
      // sibling worker — its cached state is then stale, and folding onto
      // it silently corrupts the row for good (the flushed row carries the
      // newest event_id, so the monotonic-upsert guard overwrites the
      // correct row with the wrong one). "Newer than what I hold" is not
      // the same question as "next after what I hold".
      //
      // A gap re-takes the first-sight path: drop the entry and load head
      // state, which is authoritative and absorbs both the missed window
      // and the version slot a `__snapshot__` consumes. The reloaded entry
      // sits at head, so this event then takes the already-folded branch.
      const contiguous = event.version === fold.version + 1;
      if (event.id > fold.event_id && !contiguous) {
        cache.delete(stream);
        fold = await first_sight(stream);
      }
      if (event.id > fold.event_id) {
        const reducer = me.patch[event.name as keyof TEvents];
        fold.state = patch_fn(
          me,
          fold.state,
          reducer(event as never, fold.state),
          event as never
        );
        fold.version = event.version;
        fold.event_id = event.id;
        fold.patches++;
        fold.dirty = true;
      } else {
        // Already folded (head load or redelivery) — mark dirty anyway
        // so replays repopulate the read table: this is what keeps a
        // rebuild at one upsert per stream instead of zero.
        fold.dirty = true;
      }
      if (++folded_since_flush >= config.flushEvery) {
        await flush_dirty();
        folded_since_flush = 0;
      }
    }
    // Flush before returning: the drain acks this batch's watermark only
    // after the handler resolves, so rows are durable before the ack.
    await flush_dirty();
  };

  // Dirty entries are dropped rather than flushed: a rebuild is about to
  // re-derive every row from the store, so a pending write of process-local
  // state has nothing to contribute and everything to get wrong.
  Object.defineProperty(handler, FOLD_RESET, { value: () => cache.clear() });
  return handler;
}
